import _isEmpty from 'lodash/isEmpty';

import { VenueDashboardSubtype } from '@classpass/protobuf-definitions/lib/web/models/inventory/venue_config';

import {
  CP_EMAIL_REGEX,
  MANUAL_SKU_SOURCES,
  MSI_SOURCES,
  PERMISSION,
  USER_ROLE,
  VENUE_SERVICE_TYPES,
  VENUE_SOURCE,
} from 'constants/logic';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportWarning } from 'helpers/error-logging';

export const IA_KEY = {
  ACCOUNT_STAFF: 'ACCOUNT_STAFF',
  APPOINTMENTS: 'APPOINTMENTS',
  APPOINTMENTS_PAGE: 'APPOINTMENTS_PAGE',
  ORDERS: 'ORDERS',
  APPOINTMENTS_CALENDAR_LINK: 'APPOINTMENTS_CALENDAR_LINK',
  CLASSTIVITY_SCHEDULER: 'CLASSTIVITY_SCHEDULER',
  COMPONENT_EXAMPLES: 'COMPONENT_EXAMPLES',
  ITEM_MANAGEMENT: 'ITEM_MANAGEMENT',
  ITEMS_ROUTE: 'ITEMS_ROUTE',
  JAPAN_FEATURES: 'JAPAN_FEATURES',
  MANAGE_CLASS_PAGES: 'MANAGE_CLASS_PAGES',
  MANAGE_CLASS_SCHEDULE: 'MANAGE_CLASS_SCHEDULE',
  MANAGE_DYNAMIC_PRICING: 'MANAGE_DYNAMIC_PRICING',
  MANAGE_FCF_MODE: 'MANAGE_FCF_MODE',
  MANAGE_INSTRUCTORS: 'MANAGE_INSTRUCTORS',
  MANAGE_PREMIUM_CAPS: 'MANAGE_PREMIUM_CAPS',
  MANAGE_SERVICES: 'MANAGE_SERVICES',
  EQUIPMENT: 'EQUIPMENT',
  OPPORTUNITIES: 'OPPORTUNITIES',
  CAMPAIGNS: 'CAMPAIGNS',
  RATINGS: 'RATINGS',
  SKU_ITEM_MANAGEMENT: 'SKU_ITEM_MANAGEMENT',
  SKU_ORDERS: 'SKU_ORDERS',
  SKU_PAYOUT_REPORTS: 'SKU_PAYOUT_REPORTS',
  SKU_VENUE: 'SKU_VENUE',
  SMART_RATE: 'SMART_RATE',
  SMART_SPOT: 'SMART_SPOT',
  STATS_MARKET_INSIGHTS: 'STATS_MARKET_INSIGHTS',
  STATS_REPORTS_BOOKINGS: 'STATS_REPORTS_BOOKINGS',
  STATS_REPORTS_VENUE_REVENUE: 'STATS_REPORTS_VENUE_REVENUE',
  YIELD_MANAGEMENT: 'YIELD_MANAGEMENT',
  NESTED_STATS: 'NESTED_STATS',
  STANDALONE_STATS: 'STANDALONE_STATS',
};

export function providesService(venue, service) {
  let { serviceTypes } = venue;

  // un-typed venues are assumed to be type VENUE_SERVICE_TYPES.CLASS
  if (_isEmpty(serviceTypes)) {
    serviceTypes = [VENUE_SERVICE_TYPES.CLASS];
  }

  // sometimes the type is an empty string... we consider that un-typed
  serviceTypes = serviceTypes.map(type => type || VENUE_SERVICE_TYPES.CLASS);

  return serviceTypes.includes(service);
}

function hasSource(venue, sources) {
  const { venueSource } = venue;
  return sources.includes(venueSource);
}

function hasPermission(permissions, permission) {
  const predicate = perm => permissions.includes(perm);
  if (Array.isArray(permission)) return permission.every(predicate);
  return predicate(permission);
}

export function isVenueDashboardSubtypeFoodAndBeverage(venue) {
  return venue.venueDashboardSubtype === VenueDashboardSubtype.FOOD_AND_BEVERAGE;
}

export function isVenueDynamicPricingEnabled(venue) {
  return Boolean(venue.dynamicPricingEnabled);
}

export function getVenueById(venueId) {
  return window.CP_USER.venues_list.find(v => v.venue_id === venueId);
}

export function isVenueDashboardSubtypeFoodAndBeverageById(venueId) {
  try {
    const venue = transformKeysToCamelCase(getVenueById(venueId));

    // this means the User does not have access to the Venue in question
    if (!venue) {
      return false;
    }

    return isVenueDashboardSubtypeFoodAndBeverage(venue);
  } catch (error) {
    if (venueId || window.CP_USER?.isAuthenticated) {
      reportWarning(`V2 ${error.message}`, {
        error: JSON.stringify(error),
        venueId: JSON.stringify(venueId),
        user: JSON.stringify(window.CP_USER),
        user_venues: JSON.stringify(window.CP_USER?.venues_list),
      });
    }

    return false;
  }
}

export function isSkuVenue(venue) {
  let { serviceTypes } = venue;

  if (_isEmpty(serviceTypes)) {
    serviceTypes = [VENUE_SERVICE_TYPES.CLASS];
  }

  const normalizedServiceTypes = serviceTypes.map(type => type || VENUE_SERVICE_TYPES.CLASS);
  const isExclusivelySku = normalizedServiceTypes.every(type => type === VENUE_SERVICE_TYPES.SKU);
  const SKU_ELIGIBLE_SOURCES = [VENUE_SOURCE.MANUAL_SKU, VENUE_SOURCE.SQUARE_PRODUCT];
  const isEligibleSource = SKU_ELIGIBLE_SOURCES.includes(venue.venueSource);

  return isExclusivelySku && isEligibleSource;
}

export function isManualSkuVenue(venue) {
  return isSkuVenue(venue) && hasSource(venue, MANUAL_SKU_SOURCES);
}

export function isIntegratedSkuVenue(venue) {
  return isSkuVenue(venue) && !hasSource(venue, MANUAL_SKU_SOURCES);
}

const CRITERIA = {
  [IA_KEY.ACCOUNT_STAFF]: {
    user: (_, { role }) => role === USER_ROLE.OWNER,
  },

  [IA_KEY.APPOINTMENTS]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) && !isVenueDashboardSubtypeFoodAndBeverage(venue),
  },

  [IA_KEY.APPOINTMENTS_PAGE]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT),
  },

  [IA_KEY.ORDERS]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) &&
      isVenueDashboardSubtypeFoodAndBeverage(venue) &&
      !isSkuVenue(venue),
  },

  [IA_KEY.APPOINTMENTS_CALENDAR_LINK]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) &&
      hasSource(venue, MSI_SOURCES) &&
      !isVenueDashboardSubtypeFoodAndBeverage(venue),
  },

  [IA_KEY.CLASSTIVITY_SCHEDULER]: {
    user: permissions => hasPermission(permissions, PERMISSION.FEATURE_CLASSTIVITY_SCHEDULER),
  },

  [IA_KEY.COMPONENT_EXAMPLES]: {
    user: (_, { email }) => CP_EMAIL_REGEX.test(email),
  },

  [IA_KEY.ITEM_MANAGEMENT]: {
    venue: venue =>
      isVenueDashboardSubtypeFoodAndBeverage(venue) &&
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) &&
      hasSource(venue, MSI_SOURCES) &&
      !isSkuVenue(venue),
  },

  [IA_KEY.SKU_ITEM_MANAGEMENT]: {
    venue: venue => isSkuVenue(venue),
  },

  [IA_KEY.SKU_ORDERS]: {
    venue: venue => isSkuVenue(venue),
  },

  [IA_KEY.SKU_PAYOUT_REPORTS]: {
    venue: venue => isSkuVenue(venue),
    user: permissions => hasPermission(permissions, PERMISSION.DISPLAY_RATE),
  },

  [IA_KEY.SKU_VENUE]: {
    venue: venue => isSkuVenue(venue),
  },

  [IA_KEY.ITEMS_ROUTE]: {
    venue: venue => CRITERIA[IA_KEY.ITEM_MANAGEMENT].venue(venue) || CRITERIA[IA_KEY.SKU_ITEM_MANAGEMENT].venue(venue),
  },

  [IA_KEY.JAPAN_FEATURES]: {
    venue: venue => {
      const { countryCode } = venue;
      return countryCode === 'JPN';
    },
  },

  [IA_KEY.LIVESTREAM]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS),
    user: permissions => hasPermission(permissions, PERMISSION.FEATURE_LIVESTREAM),
  },

  [IA_KEY.MANAGE_CLASS_PAGES]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS),
  },

  [IA_KEY.MANAGE_CLASS_SCHEDULE]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS) && hasSource(venue, MSI_SOURCES),
    override: permissions => hasPermission(permissions, PERMISSION.FEATURE_CLASSTIVITY_SCHEDULER),
  },

  [IA_KEY.MANAGE_INSTRUCTORS]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS) && hasSource(venue, MSI_SOURCES),
  },

  [IA_KEY.MANAGE_SERVICES]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) && !isVenueDashboardSubtypeFoodAndBeverage(venue),
  },

  [IA_KEY.EQUIPMENT]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.APPOINTMENT) && !isVenueDashboardSubtypeFoodAndBeverage(venue),
  },

  [IA_KEY.SMART_RATE]: {
    venue: venue =>
      providesService(venue, VENUE_SERVICE_TYPES.CLASS) && !venue.isManualStudio && isVenueDynamicPricingEnabled(venue),
    user: permissions =>
      hasPermission(permissions, PERMISSION.DISPLAY_RATE) &&
      !hasPermission(permissions, PERMISSION.REMOVE_SMART_RATE_VIEW),
  },

  [IA_KEY.SMART_SPOT]: {
    venue: venue =>
      venue.isSmartspotEligible || (venue.isManualStudio && providesService(venue, VENUE_SERVICE_TYPES.CLASS)),
    user: (permissions, { role }) =>
      hasPermission(permissions, PERMISSION.CAN_MANAGE_SMARTSPOT) ||
      role === USER_ROLE.MANAGER ||
      role === USER_ROLE.OWNER,
  },

  [IA_KEY.MANAGE_DYNAMIC_PRICING]: {
    user: permissions => hasPermission(permissions, PERMISSION.CAN_VIEW_DYNAMIC_PRICING),
  },

  [IA_KEY.MANAGE_FCF_MODE]: {
    user: permissions =>
      hasPermission(permissions, PERMISSION.DISPLAY_RATE) && !hasPermission(permissions, PERMISSION.HIDE_FCF_SETTINGS),
  },

  [IA_KEY.MANAGE_PREMIUM_CAPS]: {
    user: permissions => hasPermission(permissions, PERMISSION.CAN_MANAGE_PREMIUM_CAPS),
  },

  [IA_KEY.OPPORTUNITIES]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS) && !isVenueDashboardSubtypeFoodAndBeverage(venue),
    user: permissions => hasPermission(permissions, PERMISSION.DISPLAY_SPOT_MANAGEMENT),
  },

  [IA_KEY.CAMPAIGNS]: {
    user: permissions => hasPermission(permissions, PERMISSION.DISPLAY_RATE),
  },

  [IA_KEY.RATINGS]: {
    user: permissions => hasPermission(permissions, PERMISSION.FEATURE_RATINGS_REVIEWS),
  },

  [IA_KEY.STATS_MARKET_INSIGHTS]: {
    user: permissions => hasPermission(permissions, PERMISSION.FEATURE_MARKET_INSIGHTS),
  },

  [IA_KEY.STATS_REPORTS_BOOKINGS]: {
    user: permissions => hasPermission(permissions, PERMISSION.FEATURE_REPORTS_RESERVATIONS_TAB),
  },

  [IA_KEY.STATS_REPORTS_VENUE_REVENUE]: {
    user: permissions => hasPermission(permissions, PERMISSION.DISPLAY_RATE),
  },

  [IA_KEY.YIELD_MANAGEMENT]: {
    venue: venue => providesService(venue, VENUE_SERVICE_TYPES.CLASS),
    override: permissions => hasPermission(permissions, PERMISSION.FEATURE_CLASSES_OVERRIDE),
  },

  [IA_KEY.NESTED_STATS]: {
    venue: venue => !isVenueDashboardSubtypeFoodAndBeverage(venue) && !isSkuVenue(venue),
  },

  [IA_KEY.STANDALONE_STATS]: {
    venue: venue => isVenueDashboardSubtypeFoodAndBeverage(venue) && !isSkuVenue(venue),
  },
};

function test(key, userInfo = {}, activeVenueId) {
  const { venue = () => true, user = () => true, override = () => false } = CRITERIA[key] || {};
  const { permissions = [], venuesList = [{}] } = userInfo;

  let activeVenue;
  if (activeVenueId) {
    activeVenue = venuesList.find(({ venueId }) => venueId === activeVenueId) || {};
  }

  const meetsVenueCriteria = (activeVenue ? [activeVenue] : venuesList).map(venue).some(Boolean);
  const meetsUserCriteria = user(permissions, userInfo);
  const meetsOverrideCriteria = override(permissions);

  if (meetsOverrideCriteria) return true;
  if (meetsVenueCriteria && meetsUserCriteria) return true;
  return false;
}

const userHasAccess = key => userInfo => test(key, userInfo);
const featureIsSupported = key => (userInfo, activeVenueId) => test(key, userInfo, activeVenueId);

export const iaLogic = { userHasAccess, featureIsSupported };

export function shouldShowMenuEditor(userInfo, activeVenueId) {
  if (!featureIsSupported(IA_KEY.SKU_ITEM_MANAGEMENT)(userInfo, activeVenueId)) {
    return false;
  }
  const venue = userInfo.venuesList?.find(v => v.venueId === activeVenueId);
  return venue && isManualSkuVenue(venue);
}

export function shouldShowMenu(userInfo, activeVenueId) {
  if (!featureIsSupported(IA_KEY.SKU_ITEM_MANAGEMENT)(userInfo, activeVenueId)) {
    return false;
  }
  const venue = userInfo.venuesList?.find(v => v.venueId === activeVenueId);
  return venue && isIntegratedSkuVenue(venue);
}

export const forTesting = {
  providesService,
  hasSource,
  hasPermission,
  isSkuVenue,
  isManualSkuVenue,
  isIntegratedSkuVenue,
};
